import { http } from "./client";
import type { PageParams } from "@/types/api";
import type { Expense, ExpenseCategory, ExpenseCategoryStatus, ExpensePolicy, ExpenseStatus, ExpenseSummary } from "@/types/domain";

export interface ExpenseFilters extends PageParams {
  status?: ExpenseStatus | "";
  expense_category_id?: string;
  from?: string;
  to?: string;
}

export interface ExpenseCategoryFilters extends PageParams {
  status?: ExpenseCategoryStatus | "";
}

export interface SubmitExpenseInput {
  expense_category_id: string;
  amount: number;
  expense_date: string;
  description?: string;
  receipt: File;
  claim_type?: "general" | "travel" | "mileage" | "per_diem";
  trip_from?: string;
  trip_to?: string;
  travel_start?: string;
  travel_end?: string;
  travel_mode?: string;
  distance_km?: number;
  rate_per_km?: number;
  per_diem_days?: number;
  per_diem_rate?: number;
}

export const expenseApi = {
  // ---- claims ----
  my: (filters: ExpenseFilters = {}) => http.getPage<Expense>("/expenses/my", filters),
  team: (filters: ExpenseFilters = {}) => http.getPage<Expense>("/expenses/team", filters),
  reports: (filters: ExpenseFilters = {}) => http.getPage<Expense>("/expenses/reports", filters),
  summary: () => http.get<ExpenseSummary>("/expenses/summary"),
  approved: (filters: ExpenseFilters = {}) => http.getPage<Expense>("/expenses/approved", filters),
  get: (id: string) => http.get<Expense>(`/expenses/${id}`),
  receiptUrl: (id: string) => `/expenses/${id}/receipt`,
  downloadReceipt: (id: string) => http.download(`/expenses/${id}/receipt`),

  submit: (input: SubmitExpenseInput) => {
    const form = new FormData();
    form.set("expense_category_id", input.expense_category_id);
    form.set("amount", String(input.amount));
    form.set("expense_date", input.expense_date);
    form.set("description", input.description ?? "");
    form.set("receipt", input.receipt);
    // Travel claim fields - only the ones that apply are sent.
    for (const key of ["claim_type", "trip_from", "trip_to", "travel_start", "travel_end", "travel_mode", "distance_km", "rate_per_km", "per_diem_days", "per_diem_rate"] as const) {
      const v = input[key];
      if (v !== undefined && v !== null && v !== "") form.set(key, String(v));
    }
    return http.post<Expense>("/expenses", form);
  },

  approve: (id: string, comments?: string) => http.put<Expense>(`/expenses/${id}/approve`, { comments }),
  reject: (id: string, comments?: string) => http.put<Expense>(`/expenses/${id}/reject`, { comments }),
  markPaid: (id: string) => http.put<Expense>(`/expenses/${id}/payment`, {}),

  // ---- categories ----
  categories: (filters: ExpenseCategoryFilters = {}) => http.getPage<ExpenseCategory>("/expense-categories", filters),
  createCategory: (values: { name: string; description?: string; status?: ExpenseCategoryStatus }) =>
    http.post<ExpenseCategory>("/expense-categories", values),
  updateCategory: (id: string, values: Partial<{ name: string; description: string; status: ExpenseCategoryStatus }>) =>
    http.put<ExpenseCategory>(`/expense-categories/${id}`, values),

  // ---- policies ----
  policies: () => http.get<ExpensePolicy[]>("/expense-policies"),
  savePolicy: (values: { category_id: string; maximum_limit?: number; approval_required?: boolean; second_level_threshold?: number | null; clear_second_level_threshold?: boolean }) =>
    http.put<ExpensePolicy>("/expense-policies", values),

};
